import Logo from "@/assets/assurance_maladie.svg";
import ServiceLogo from "@/assets/service_trajet_sante.svg";
import { TopBar } from "@components/layout/TopBar";
import { routes } from "@routes";
import { useMatch, useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { ProfileMenu } from "@components/common/ProfileMenu";
import { ProfileMenuMobile } from "@components/common/ProfileMenuMobile";
import { ory } from "@services/authentication";
import { Button, Icon, Spinner } from "@thetreep/ui";
import { useTranslation } from "react-i18next";
import theme from "@theme";
import { useUserStore } from "@stores/userStore";
import { useCartStore } from "@stores/cartStore";
import { useToaster } from "@hooks/useToaster";
import { ConnectError } from "@connectrpc/connect";
import { useNavigationGuard } from "@hooks/useNavigationGuard";
import { WarningModal } from "@pages/booking/exchange/WarningModal";
import { useBookingStore } from "@stores/useBookingStore";

export interface HeaderProps {
  className?: string;
}

export function Header({ className }: HeaderProps) {
  const navigate = useNavigate();
  const { user, isInitialized, clearUser } = useUserStore();
  const { t } = useTranslation();
  const { cart, fetchCart, clearCart } = useCartStore();
  const { resetBookingForm } = useBookingStore();
  const { addToast } = useToaster();

  const onLogout = async () => {
    try {
      const { data: flow } = await ory().createBrowserLogoutFlow();
      await ory().updateLogoutFlow({
        token: flow.logout_token,
      });
      clearUser(); // Effacer l'utilisateur du store
      clearCart();
      resetBookingForm();
      navigate(routes.LOGIN);
    } catch (error) {
      console.error("error logging out", error);
    }
  };

  const isExchange = useMatch(routes.EXCHANGE);
  const isExchangeSearch = useMatch(routes.EXCHANGE_SEARCH);
  const isExchangeResult = useMatch(routes.EXCHANGE_RESULTS);
  const isExchangeRecap = useMatch(routes.EXCHANGE_RECAP);

  // ou autre logique pour savoir si on doit bloquer
  const shouldBlock = Boolean(
    isExchange || isExchangeSearch || isExchangeResult || isExchangeRecap,
  );
  const [blockNavigation, setBlockNavigation] = useState(shouldBlock);
  const [prevShouldBlock, setPrevShouldBlock] = useState(shouldBlock);
  if (shouldBlock !== prevShouldBlock) {
    setPrevShouldBlock(shouldBlock);
    setBlockNavigation(shouldBlock);
  }

  const { handleNavigate, showPrompt, confirmNavigation, cancelNavigation } =
    useNavigationGuard(blockNavigation);

  const onNavigate = (id: string) => {
    if (id === "home") {
      handleNavigate(routes.HOME);
    } else if (id === "search") {
      handleNavigate(routes.SEARCH);
    } else if (id === "dap") {
      handleNavigate(routes.DAP_MANAGEMENT);
    } else if (id === "caisse") {
      handleNavigate(routes.CAISSE_MANAGEMENT);
    } else if (id === "user") {
      handleNavigate(routes.USER_MANAGEMENT);
    } else if (id === "agency") {
      handleNavigate(routes.AGENCY_MANAGEMENT);
    } else if (id === "bookings") {
      handleNavigate(routes.USER_BOOKINGS);
    } else if (id === "settings") {
      handleNavigate(routes.SETTINGS);
    }
  };

  useEffect(() => {
    if (user) {
      try {
        fetchCart();
      } catch (e) {
        if (e instanceof ConnectError && e.rawMessage) {
          addToast("error", e.rawMessage);
        } else {
          addToast("error", t("genericError"));
        }
      }
    }
  }, [user]);

  return (
    <div
      className={` fixed top-0 right-0 left-0 z-[1000] border-b border-border-soft ${className}`}
    >
      <TopBar />

      <div className="w-full h-16 px-5 md:px-8 md:h-28 lg:px-24 xl:px-48 bg-white border-b border-[#9ba8c8] justify-between items-center flex">
        <div className="justify-start items-center gap-4 flex flew-nowrap">
          <img
            src={Logo}
            alt="L'assurance Maladie"
            className="max-w-[173px] w-[82px] md:w-[123px] lg:w-full h-auto cursor-pointer"
            onClick={() => handleNavigate("/")}
          />
          <div className="w-[1px] h-[30px]  md:h-[62px]  bg-[#1c4094]"></div>
          <img
            src={ServiceLogo}
            alt="Service Trajet Santé"
            className="max-w-[130px] w-[62px] md:w-[93px] lg:w-full   h-auto "
          />
        </div>
        {!isInitialized ? (
          <Spinner />
        ) : user ? (
          <>
            {/* Desktop right menu */}
            <div className="hidden md:flex justify-start items-center gap-10 flex-nowrap">
              <div
                className="cursor-pointer text-[#1c4094] text-base font-bold"
                onClick={() => handleNavigate(routes.SEARCH)}
              >
                {t("header.bookJourney")}
              </div>

              <button onClick={() => handleNavigate(routes.CART)}>
                <div className="relative hover:scale-105">
                  <div className="hover:scale-105 active:scale-110">
                    <Icon src="basket_fill" size={24} />
                  </div>
                  {cart && cart.length > 0 && (
                    <div
                      className="absolute top-[-12px] right-[-12px] 
                    bg-cnam-pink rounded-full px-[6px] py-[2px] z-10 shadow-[0_0_8px_2px_rgba(0,0,0,0.15)]
                    flex items-center justify-center gap-2"
                    >
                      <span className="text-xs text-cnam font-bold">
                        {cart.length}
                      </span>
                    </div>
                  )}
                </div>
              </button>

              <ProfileMenu
                user={user}
                onLogout={onLogout}
                onNavigate={onNavigate}
              />
            </div>

            {/* Mobile right menu */}
            <div className="md:hidden items-center gap-10 flex">
              <Icon
                onClick={() => navigate(routes.CART)}
                src="basket_fill"
                size={24}
              />

              <ProfileMenuMobile
                user={user}
                onLogout={onLogout}
                onNavigate={onNavigate}
              />
            </div>
          </>
        ) : (
          <Button
            colors={{
              backgroundColor: theme.colors.cnam.blue.DEFAULT,
              color: theme.colors.white,
            }}
            size="compact"
            onClick={() => navigate(routes.LOGIN)}
          >
            {" "}
            {t("header.connect")}
          </Button>
        )}

        {showPrompt && (
          <WarningModal
            onConfirm={confirmNavigation}
            onClose={cancelNavigation}
            isOpen={showPrompt}
          />
        )}
      </div>
    </div>
  );
}
